import { InlineNotice } from "@rosc/ui/v32/notice";
import { Link, useNavigate } from "@tanstack/react-router";
import {
  Check,
  MessageCircleMore,
  MessageSquareText,
  ShieldAlert,
} from "lucide-react";
import type { FunctionComponent, JSX } from "react";
import { useId, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { AppConfig } from "@/common/appConfig";
import { Button } from "@/components/ui/button";
import { WithDictation } from "@/components/ui/DictationButton";
import { Form, FormControl, FormItem } from "@/components/ui/form";
import { FormField } from "@/components/ui/form/FormField";
import { Textarea } from "@/components/ui/textarea";
import { V3Button } from "@/components/ui/v3/button";
import { V3_INPUT } from "@/components/ui/v3/flagged";
import { StandaloneFrame } from "@/components/ui/v3/standalone-frame";
import {
  FEEDBACK_MESSAGE_MAX,
  type FeedbackSendError,
  feedbackSendError,
  sendFeedbackReport,
} from "@/lib/in-app-feedback";
import { useVisitScope } from "@/lib/visit-queries";
import type { FeedbackFormSchemaType } from "@/schemas/feedback.schema";

export const FeedbackPage: FunctionComponent = () =>
  AppConfig.enableAppRedesign ? <FeedbackPageV3 /> : <ClassicFeedbackPage />;

// v3.2 (VITE_APP_REDESIGN_ENABLED): the same report as the Send feedback
// dialog (POST /api/v2/feedback/reports), on its own page: a message, the
// privacy note, one ink "Send feedback" and a quiet way back.
export const FeedbackPageV3 = ({
  onSend = sendFeedbackReport,
}: {
  onSend?: typeof sendFeedbackReport;
}): JSX.Element => {
  const { t } = useTranslation();
  const scope = useVisitScope();
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<FeedbackSendError | null>(null);
  const ids = { message: useId(), privacy: useId(), count: useId() };
  const trimmed = message.trim();
  const canSend = !!scope && trimmed.length > 0 && !sending;

  const submit = async (): Promise<void> => {
    if (!canSend || !scope) return;
    setSending(true);
    setError(null);
    try {
      await onSend({ type: "other", message: trimmed }, scope.uid);
      setSent(true);
    } catch (reason) {
      setError(feedbackSendError(reason));
    } finally {
      setSending(false);
    }
  };

  const back = (
    <V3Button
      asChild
      className="w-full sm:w-auto"
      variant={sent ? "primary" : "ghost"}
    >
      <Link data-testid="feedback-page-back" to="/visits">
        {t("feedback.page.back")}
      </Link>
    </V3Button>
  );

  return (
    <StandaloneFrame align="start">
      {sent ? (
        <div
          className="text-center"
          data-testid="feedback-page-sent"
          role="status"
        >
          <span
            aria-hidden
            className="mx-auto flex size-12 items-center justify-center rounded-full bg-v3-ok-tint text-v3-ok-text"
          >
            <Check className="size-6" strokeWidth={2.5} />
          </span>
          <p className="mt-4 text-[15px] font-medium leading-relaxed text-v3-text">
            {t("feedback.inApp.sent")}
          </p>
          <div className="mt-6 flex justify-center">{back}</div>
        </div>
      ) : (
        <form
          noValidate
          onSubmit={(event) => {
            event.preventDefault();
            void submit();
          }}
        >
          <div className="flex items-center gap-3.5">
            <span
              aria-hidden
              className="flex size-10 shrink-0 items-center justify-center rounded-[12px] bg-v3-surface-2 text-v3-text shadow-v3-hairline"
            >
              <MessageSquareText className="size-5" />
            </span>
            <div className="min-w-0">
              <h1 className="text-[17px] font-semibold leading-6 text-v3-text">
                {t("feedback.inApp.title")}
              </h1>
              <p className="mt-0.5 text-[13.5px] leading-snug text-v3-text-2">
                {t("feedback.inApp.subtitle")}
              </p>
            </div>
          </div>
          <div className="mt-5 mb-2 flex items-baseline justify-between gap-2">
            <label
              className="text-[13px] font-medium text-v3-text-2"
              htmlFor={ids.message}
            >
              {t("feedback.inApp.messageLabel")}
            </label>
            <span
              className="text-[12px] tabular-nums text-v3-text-3"
              id={ids.count}
            >
              {t("feedback.inApp.characters", {
                count: message.length,
                max: FEEDBACK_MESSAGE_MAX,
              })}
            </span>
          </div>
          <WithDictation
            multiline
            fieldLabel={t("dictation.field.fields.feedback")}
          >
            <textarea
              aria-describedby={`${ids.privacy} ${ids.count}`}
              aria-required="true"
              className={`${V3_INPUT} block min-h-[160px] w-full resize-y rounded-[12px] px-3.5 py-3 leading-relaxed focus-visible:outline-hidden`}
              data-testid="feedback-page-message"
              disabled={sending}
              id={ids.message}
              maxLength={FEEDBACK_MESSAGE_MAX}
              placeholder={t("feedback.inApp.placeholders.other")}
              value={message}
              onChange={(event) => {
                setMessage(event.target.value);
                if (error) setError(null);
              }}
            />
          </WithDictation>
          <InlineNotice
            className="mt-3"
            icon={<ShieldAlert />}
            id={ids.privacy}
            tone="warn"
          >
            {t("feedback.inApp.privacy")}
          </InlineNotice>
          {!scope && (
            <p
              className="mt-3 text-[13.5px] text-v3-text-2"
              data-testid="feedback-page-signin"
            >
              {t("feedback.page.signIn")}
            </p>
          )}
          {error && (
            <InlineNotice className="mt-3" role="alert" tone="error">
              {t(`feedback.inApp.errors.${error}`)}
            </InlineNotice>
          )}
          <div className="mt-6 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
            {back}
            {scope ? (
              <V3Button
                aria-disabled={!canSend}
                className="w-full aria-disabled:cursor-not-allowed aria-disabled:opacity-40 sm:w-auto"
                data-testid="feedback-page-send"
                type="submit"
                variant="primary"
              >
                {sending
                  ? t("feedback.inApp.sending")
                  : t("feedback.inApp.send")}
              </V3Button>
            ) : (
              <V3Button asChild className="w-full sm:w-auto" variant="primary">
                <Link to="/">{t("feedback.page.signInAction")}</Link>
              </V3Button>
            )}
          </div>
        </form>
      )}
    </StandaloneFrame>
  );
};

const ClassicFeedbackPage: FunctionComponent = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const form = useForm<FeedbackFormSchemaType>({
    defaultValues: {
      feedback: "",
    },
  });

  const {
    formState: { isValid },
  } = form;

  const handleGoBack = async (): Promise<void> => {
    await navigate({ to: "/visits" });
  };

  const handleSendFeedback = async (): Promise<void> => {};

  return (
    <StandaloneFrame width="md:w-[70%] lg:w-[50%] w-[90%]">
      <Form {...form}>
        <div className="flex justify-center mb-4">
          <MessageCircleMore color="#6F7293" />
        </div>

        <h6>{t("feedback.title")}</h6>

        <p className="text-tinted-grey-3 mt-2">{t("feedback.description")}</p>

        <FormField
          control={form.control}
          name="feedback"
          render={({ field }) => (
            <FormItem>
              <WithDictation
                multiline
                className="mt-4"
                fieldLabel={t("dictation.field.fields.feedback")}
              >
                <FormControl>
                  <Textarea
                    placeholder={t("feedback.textAreaPlaceholder")}
                    {...field}
                  />
                </FormControl>
              </WithDictation>
            </FormItem>
          )}
        />

        <div className="flex justify-center">
          <div className="flex gap-4">
            <Button
              className="mt-6"
              size="lg"
              type="submit"
              onClick={handleGoBack}
            >
              {t("feedback.backBtn")}
            </Button>

            <Button
              className="mt-6"
              disabled={!isValid}
              size="lg"
              type="submit"
              onClick={handleSendFeedback}
            >
              {t("feedback.sendBtn")}
            </Button>
          </div>
        </div>
      </Form>
    </StandaloneFrame>
  );
};
